import { StatusConfig } from './registration-status.model';
import { School, Lunch, Module } from './schools.model';
import { LanguageCode } from './language.model';
import { DocumentReference, Timestamp } from 'firebase/firestore';

export interface Contract {
  // Core contract metadata
  school: DocumentReference | School;
  status: ContractStatus;
  currentSchoolYear: number;
  createdOn: Timestamp;
  updatedOn: Timestamp;
  cancelledOn?: Timestamp;
  cancellationEffectiveDate?: Timestamp; // When cancellation will take effect (if status is cancellationRequested)
  cancellationAlterationId?: string | null; // Verweis auf den Kündigungsantrag (alterations/{id}), für die Chip-Verlinkung (#897)
  validFrom: Timestamp;
  validUntil: Timestamp; // Contract end date (calculated as July 31st + X years from start)
  language: LanguageCode;

  // Export tracking
  exported?: boolean;
  exportedOn?: Timestamp;

  // Papiervertrag (#870): Vertrag wurde nicht im Elternportal abgeschlossen,
  // sondern liegt samt SEPA-Mandat unterschrieben auf Papier vor. Ohne
  // `paymentInfo` — Bankdaten stehen nur auf dem Papierformular.
  paperContract?: boolean;
  paperContractRecordedOn?: Timestamp;
  paperContractRecordedBy?: string;
  paperContractNote?: string;

  // Contract lifecycle management
  history: Array<{
    schoolYear: string;
    updatedOn: Timestamp;
  }>;
  renewalInfo?: {
    dueDate: Timestamp;
    remindersSent: Array<{
      sentOn: Timestamp;
      success: boolean;
    }>;
  };

  // Contract content (merged from Contract model)
  // Optional: Papierverträge (#870) entstehen ohne Portal-Formular und tragen
  // deshalb keine reviewInfo. Gelesen wird sie nirgends.
  reviewInfo?: ReviewInfo;
  legal: LegalConfirmation;
}

// Interfaces from the old Contract model
export interface LegalConfirmation {
  hasAccepted: boolean;
  acceptedOn: Timestamp;
  version: string;
}

export interface ReviewInfo {
  selectedSchool: School;
  selectedModule: Module;
  selectedLunch: Lunch | null;
}

/**
 * Billing adjustment information for module/lunch changes
 * This is for informational purposes only - actual billing is handled by accounting
 */
export interface BillingAdjustment {
  adjustmentAmount: number; // Positive = additional payment needed, Negative = credit/refund
  oldMonthlyFee: number; // Old monthly fee (module + lunch if included)
  newMonthlyFee: number; // New monthly fee (module + lunch if included)
  monthlyDifference: number; // Monthly difference (new - old)
  remainingMonths: number; // Remaining months until contract end
  effectiveDate: Timestamp; // When the change takes effect
  calculatedOn: Timestamp; // When this was calculated
  reason: string; // German description of the change
}

export type ContractStatus =
  | 'active'
  | 'renewalPending'
  | 'terminated'
  | 'cancellationRequested'
  | 'cancellationApproved'
  | 'cancelled';

export const ContractStatusConfigs: Record<ContractStatus, StatusConfig> = {
  active: {
    type: 'info',
    label: $localize`:@@lib.enrollment.active:Aktiv`,
    action: $localize`:@@lib.enrollment.activeAction:Der Vertrag wurde abgeschlossen und ist aktiv.`,
    styles: { background: 'var(--theme-success)', color: '#fff' },
  },

  renewalPending: {
    type: 'warning',
    label: $localize`:@@lib.enrollment.renewalPending:Verlängerung`,
    action: $localize`:@@lib.enrollment.renewalPendingAction:Klicken Sie hier, den Vertrag für das kommende Schuljahr zu bestätigen.`,
    styles: { background: 'var(--theme-yellow)', color: '#fff' },
  },

  terminated: {
    type: 'error',
    label: $localize`:@@lib.enrollment.terminated:Gelöscht`,
    action: $localize`:@@lib.enrollment.terminatedAction:Ihr Vertrag wurde gelöscht.`,
    styles: { background: 'var(--theme-warn)', color: '#fff' },
  },

  cancellationRequested: {
    type: 'warning',
    label: $localize`:@@lib.enrollment.cancellationRequested:Kündigung beantragt`,
    action: $localize`:@@lib.enrollment.cancellationRequestedAction:Sie haben eine Kündigung beantragt. Diese wird geprüft.`,
    styles: { background: 'var(--theme-orange)', color: '#fff' },
  },

  cancellationApproved: {
    type: 'warning',
    label: $localize`:@@lib.enrollment.cancellationApproved:Kündigung genehmigt`,
    action: $localize`:@@lib.enrollment.cancellationApprovedAction:Ihre Kündigung wurde genehmigt.`,
    styles: { background: 'var(--theme-orange)', color: '#fff' },
  },

  cancelled: {
    type: 'error',
    label: $localize`:@@lib.enrollment.cancelled:Gekündigt`,
    action: $localize`:@@lib.enrollment.cancelledAction:Ihr Vertrag wurde gekündigt.`,
    styles: { background: 'var(--theme-error)', color: '#fff' },
  },
};

export const ContractStatusOptions = [
  { value: '', label: $localize`:@@lib.enrollment.all:Alle` },
  ...Object.entries(ContractStatusConfigs).map(([value, config]) => ({
    value,
    label: config.label,
  })),
];

// Backward compatibility exports
export type StudentContract = Contract;
